// src/lib/vigilabI18n.ts
//
// Langue de la modale Vigilab (KN-1557).
//
// La modale est ouverte depuis Vigilab par des analystes francophones : à la
// première ouverture elle est en français, quelle que soit la langue du
// navigateur. Le choix fait dans la modale est ensuite mémorisé sous sa propre
// clé (`vigilabLanguage`) et sert aussi de langue au rapport DD0 demandé (en-tête
// `X-Locale` envoyé par src/services/vigilabDisambiguation.ts).
//
// Une instance i18next distincte de celle de l'application (src/lib/i18n.ts) :
// changer la langue de l'instance globale l'inscrirait dans le cookie du
// détecteur, et toute l'application basculerait avec la modale. Celle-ci est
// fournie aux composants de la modale par VigilabI18nProvider ; les composants
// partagés qu'elle affiche (useTranslation) la reçoivent par le contexte.
import { createInstance } from "i18next";
import Backend from "i18next-http-backend";
import {
  FALLBACK_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from "@/lib/languages";
import { missingKeyOptions } from "@/lib/i18nMissingKey";

export const VIGILAB_LANGUAGE_STORAGE_KEY = "vigilabLanguage";

export const VIGILAB_DEFAULT_LANGUAGE: SupportedLanguage = "fr";

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly unknown[]).includes(value);
}

/** Langue choisie lors d'une ouverture précédente, sinon le français. */
function readStoredLanguage(): SupportedLanguage {
  try {
    const stored = window.localStorage.getItem(VIGILAB_LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored)) return stored;
  } catch {
    // Rendu serveur, stockage bloqué (iframe tierce, navigation privée) : défaut.
  }
  return VIGILAB_DEFAULT_LANGUAGE;
}

// Tenue à part de l'instance : elle est connue dès le chargement du module, avant
// la fin de l'initialisation asynchrone d'i18next, et c'est elle qui part au back.
let currentLanguage: SupportedLanguage = readStoredLanguage();

const vigilabI18n = createInstance();
let initialized: Promise<unknown> | null = null;

/**
 * Initialise l'instance au premier usage : le module est importé par
 * AuthProvider (VigilabSessionClosed) sur toutes les pages de l'application, qui
 * n'ont pas à charger les catalogues pour une modale qu'elles n'affichent pas.
 */
export function ensureVigilabI18n() {
  if (initialized) return vigilabI18n;
  initialized = vigilabI18n.use(Backend).init({
    lng: currentLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    // Les deux catalogues sont chargés d'emblée : basculer ne fait pas clignoter la
    // modale en attendant le second.
    preload: [...SUPPORTED_LANGUAGES],
    debug: false,
    ...missingKeyOptions(),
    backend: {
      loadPath: `/locales/{{lng}}/{{ns}}.json?v=${
        process.env.NEXT_PUBLIC_DEPLOYED_AT || Date.now()
      }`,
    },
    interpolation: {
      escapeValue: false, // React protège déjà du XSS
    },
  });
  return vigilabI18n;
}

/**
 * Résolue quand les catalogues sont chargés. Pour un texte produit hors du
 * fournisseur (page Triton) avant leur arrivée, qui afficherait sinon la clé.
 */
export function whenVigilabI18nReady(): Promise<unknown> {
  ensureVigilabI18n();
  return initialized!.catch(() => null);
}

/** Langue courante de la modale : interface et rapport DD0. */
export function vigilabLanguage(): SupportedLanguage {
  return currentLanguage;
}

/** Change la langue de la modale et la mémorise pour les ouvertures suivantes. */
export async function changeVigilabLanguage(language: SupportedLanguage): Promise<void> {
  currentLanguage = language;
  try {
    window.localStorage.setItem(VIGILAB_LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Stockage indisponible : le choix vaut pour cette ouverture seulement.
  }
  await ensureVigilabI18n().changeLanguage(language);
}

/** Locale de formatage des dates, alignée sur le reste de l'application. */
export function vigilabDateLocale(language: string = currentLanguage): string {
  return language.startsWith("en") ? "en-US" : "fr-FR";
}

/**
 * Traduction hors composant (service, hooks) : le texte suit la langue de la
 * modale au moment où il est produit.
 */
export function vigilabT(key: string, options?: Record<string, unknown>): string {
  return ensureVigilabI18n().t(key, options ?? {}) as string;
}

export default vigilabI18n;
